import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Zap, 
  Filter, 
  Calendar,
  ChevronRight,
  Trophy,
  Activity
} from 'lucide-react';
import api from '../services/api';

const Performance = () => {
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all'); // all, weekly, monthly

  useEffect(() => {
    fetchPerformance();
  }, [dateRange]);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      // In a real app, we'd pass dates based on range
      const data = await api.getTeamPerformance();
      setPerformanceData(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching performance:', error);
      setLoading(false);
    }
  };

  const topPerformer = [...performanceData].sort((a, b) => b.efficiency - a.efficiency)[0];
  const avgEfficiency = performanceData.length > 0 
    ? Math.round(performanceData.reduce((acc, curr) => acc + curr.efficiency, 0) / performanceData.length)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-tg-orange-200 border-t-tg-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-black uppercase tracking-widest text-[10px]">Analyzing Performance Matrix...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-tg-orange-500 rounded-2xl shadow-lg shadow-tg-orange-500/20">
              <TrendingUp size={24} className="text-white" />
            </div>
            <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
              Strategic <span className="text-tg-orange-500">Efficiency</span>
            </h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 font-bold tracking-wide uppercase text-[10px] pl-1">
            Analyzing operational throughput and deadline compliance
          </p>
        </motion.div>

        <div className="flex items-center gap-4">
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
            {['all', 'weekly', 'monthly'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                  dateRange === range 
                  ? 'bg-white dark:bg-slate-900 text-tg-orange-500 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard 
          icon={Activity} 
          label="Team Efficiency" 
          value={`${avgEfficiency}%`} 
          color="orange"
          subValue="Across all missions"
        />
        <StatsCard 
          icon={Trophy} 
          label="Top Performer" 
          value={topPerformer?.name || 'N/A'} 
          color="blue"
          subValue={`${topPerformer?.efficiency || 0}% Accuracy`}
        />
        <StatsCard 
          icon={CheckCircle} 
          label="Total Completed" 
          value={performanceData.reduce((acc, curr) => acc + curr.completedTasks, 0)} 
          color="emerald"
          subValue="Operations finalized"
        />
      </div>

      {/* Performance Matrix */}
      <div className="glass-card overflow-hidden">
        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
          <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-800 dark:text-white">Personnel Progress Matrix</h2>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Early</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">On Time</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Late</span>
            </div>
          </div>
        </div>
        
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          <AnimatePresence>
            {performanceData.map((member, idx) => (
              <PerformanceRow key={member._id} member={member} idx={idx} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const StatsCard = ({ icon: Icon, label, value, color, subValue }) => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-card p-6 flex items-center gap-6 group hover:border-tg-orange-500/30 transition-all"
  >
    <div className={`p-4 rounded-2xl bg-${color}-500/10 text-${color}-500 group-hover:scale-110 transition-transform`}>
      <Icon size={24} />
    </div>
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{value}</p>
      <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase tracking-tight">{subValue}</p>
    </div>
  </motion.div>
);

const PerformanceRow = ({ member, idx }) => {
  const earlyPct = member.completedTasks > 0 ? (member.early / member.completedTasks) * 100 : 0;
  const onTimePct = member.completedTasks > 0 ? (member.onTime / member.completedTasks) * 100 : 0;
  const latePct = member.completedTasks > 0 ? (member.late / member.completedTasks) * 100 : 0;

  return (
    <motion.div 
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: idx * 0.05 }}
      className="p-8 grid grid-cols-1 lg:grid-cols-12 items-center gap-8 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-all"
    >
      <div className="lg:col-span-3 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-black text-tg-orange-500 border border-slate-200 dark:border-slate-700">
          {member.name.charAt(0)}
        </div>
        <div>
          <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight">{member.name}</h4>
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{member.role}</p>
        </div>
      </div>

      <div className="lg:col-span-2 grid grid-cols-2 gap-4 text-center">
        <div>
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Assigned</p>
          <p className="text-sm font-black text-slate-900 dark:text-white">{member.totalAssigned}</p>
        </div>
        <div>
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Finalized</p>
          <p className="text-sm font-black text-slate-900 dark:text-white">{member.completedTasks}</p>
        </div>
      </div>

      <div className="lg:col-span-5 space-y-3">
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-4">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Efficiency Threshold</p>
            <div className="flex gap-2">
              <span className="text-[8px] font-black text-blue-500 uppercase tracking-widest">{member.early} Early</span>
              <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">{member.onTime} On Time</span>
              <span className="text-[8px] font-black text-red-500 uppercase tracking-widest">{member.late} Late</span>
            </div>
          </div>
          <span className={`text-[10px] font-black ${member.efficiency > 80 ? 'text-emerald-500' : member.efficiency > 50 ? 'text-amber-500' : 'text-red-500'}`}>
            {member.efficiency}%
          </span>
        </div>
        <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full flex overflow-hidden p-0.5 border border-slate-200 dark:border-slate-700">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${earlyPct}%` }}
            className="h-full bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" 
          />
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${onTimePct}%` }}
            className="h-full bg-emerald-500 rounded-full mx-0.5 shadow-[0_0_10px_rgba(16,185,129,0.5)]" 
          />
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${latePct}%` }}
            className="h-full bg-red-500 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.5)]" 
          />
        </div>
      </div>

      <div className="lg:col-span-2 flex justify-end">
        <div className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
          member.efficiency > 80 ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 
          member.efficiency > 50 ? 'bg-amber-50 text-amber-600 border-amber-100' : 
          'bg-red-50 text-red-600 border-red-100'
        }`}>
          {member.efficiency > 80 ? 'Optimal' : member.efficiency > 50 ? 'Steady' : 'Sub-Par'}
        </div>
      </div>
    </motion.div>
  );
};

export default Performance;
